import { PHYSICS, SESSION_LENGTH_S } from '../utils/constants';

/** Ring fills at nitro max so boost past base top speed still reads on the dial. */
const DISPLAY_MAX_KPH = PHYSICS.NITRO_MAX_SPEED_KMH;

/**
 * prd2 Day 7 — neon HUD: timer, heat/wanted, speed tiers, nitro cooldown, abilities row.
 */
export class DrivingHud {
  private readonly speedValue: HTMLElement | null;
  private readonly speedRing: SVGCircleElement | null;
  private readonly root: HTMLElement | null;
  private readonly shell: HTMLElement | null;
  private readonly driftPill: HTMLElement | null;
  private readonly nitroFill: HTMLElement | null;
  private readonly nitroCdFill: HTMLElement | null;
  private readonly timerText: HTMLElement | null;
  private readonly timerFill: HTMLElement | null;
  private readonly stars: HTMLElement | null;
  private readonly speedBarFill: HTMLElement | null;
  private readonly ringCircumference: number;
  private readonly comboMult: HTMLElement | null;
  private readonly comboChain: HTMLElement | null;
  private readonly comboWrap: HTMLElement | null;
  private readonly contractWrap: HTMLElement | null;
  private readonly contractTitle: HTMLElement | null;
  private readonly contractProgress: HTMLElement | null;

  constructor() {
    this.shell = document.getElementById('hud-shell');
    this.root = document.getElementById('hud-root');
    this.speedValue = document.getElementById('hud-speed');
    this.driftPill = document.getElementById('hud-drift');
    this.nitroFill = document.getElementById('hud-nitro-fill');
    this.nitroCdFill = document.getElementById('hud-nitro-cd-fill');
    this.timerText = document.getElementById('hud-timer-text');
    this.timerFill = document.getElementById('hud-timer-fill');
    this.stars = document.getElementById('hud-stars');
    this.speedBarFill = document.getElementById('hud-speed-bar-fill');
    const ring = document.getElementById('speed-ring-fill');
    this.speedRing = ring instanceof SVGCircleElement ? ring : null;
    this.comboWrap = document.getElementById('hud-combo');
    this.comboMult = document.getElementById('hud-combo-mult');
    this.comboChain = document.getElementById('hud-combo-chain');
    this.contractWrap = document.getElementById('hud-contract');
    this.contractTitle = document.getElementById('hud-contract-title');
    this.contractProgress = document.getElementById('hud-contract-progress');

    const r = this.speedRing?.r.baseVal.value ?? 44;
    this.ringCircumference = 2 * Math.PI * r;
    if (this.speedRing) {
      this.speedRing.style.strokeDasharray = `${this.ringCircumference} ${this.ringCircumference}`;
      this.speedRing.style.strokeDashoffset = String(this.ringCircumference);
    }
  }

  update(
    kph: number,
    handbrake: boolean,
    throttle: number,
    nitroTank: number,
    drifting: boolean,
    nitroBoost: boolean,
    nitroCooldownS: number,
    sessionRemainingS: number,
    wantedStars: number,
    bustMeter: number,
    comboMultiplier: number = 1,
    comboChain: number = 0,
    comboActive: boolean = false,
  ): void {
    const k = Math.max(0, Math.round(kph));
    if (this.speedValue) this.speedValue.textContent = String(k);

    const t = Math.min(1, kph / DISPLAY_MAX_KPH);
    if (this.speedRing) {
      this.speedRing.style.strokeDashoffset = String(
        this.ringCircumference * (1 - t),
      );
    }

    if (this.speedBarFill) {
      this.speedBarFill.style.transform = `scaleX(${t})`;
    }

    let tier: 's0' | 's1' | 's2' | 's3' | 's4' = 's0';
    if (kph < 60) tier = 's0';
    else if (kph < 120) tier = 's1';
    else if (kph < 160) tier = 's2';
    else if (kph < 200) tier = 's3';
    else tier = 's4';

    if (this.shell) {
      this.shell.dataset.speedTier = tier;
      this.shell.dataset.nitro = nitroBoost ? 'boost' : 'idle';
      this.shell.dataset.wanted = String(
        Math.max(0, Math.min(5, Math.round(wantedStars))),
      );
      const b = Math.max(0, Math.min(1, bustMeter));
      this.shell.dataset.bust = String(b);
      this.shell.dataset.danger =
        b > 0.55 ? 'high' : b > 0.22 ? 'mid' : 'low';
    }

    if (this.root) {
      this.root.dataset.handbrake = handbrake ? 'on' : 'off';
      this.root.dataset.throttle =
        throttle > 0.05 ? 'fwd' : throttle < -0.05 ? 'rev' : 'idle';
      this.root.dataset.nitro = nitroBoost ? 'boost' : 'idle';
    }

    if (this.driftPill) {
      this.driftPill.dataset.active = drifting ? 'true' : 'false';
    }

    if (this.nitroFill) {
      const fill = Math.max(0, Math.min(1, nitroTank));
      this.nitroFill.style.transform = `scaleX(${fill})`;
    }

    if (this.nitroCdFill) {
      const cd = PHYSICS.NITRO_COOLDOWN_S;
      const frac = cd > 1e-6 ? Math.min(1, nitroCooldownS / cd) : 0;
      this.nitroCdFill.style.transform = `scaleX(${frac})`;
      this.nitroCdFill.dataset.active = nitroCooldownS > 0.04 ? 'true' : 'false';
    }

    if (this.timerText) {
      const sec = Math.max(0, sessionRemainingS);
      const m = Math.floor(sec / 60);
      const s = Math.floor(sec % 60);
      this.timerText.textContent = `${m}:${s.toString().padStart(2, '0')}`;
    }

    if (this.timerFill) {
      const u = Math.max(0, Math.min(1, sessionRemainingS / SESSION_LENGTH_S));
      this.timerFill.style.transform = `scaleX(${u})`;
    }

    if (this.stars) {
      const w = Math.max(0, Math.min(5, Math.round(wantedStars)));
      this.stars.dataset.fill = String(w);
      for (let i = 0; i < 5; i++) {
        const el = this.stars.children[i] as HTMLElement | undefined;
        if (el) el.dataset.on = i < w ? 'true' : 'false';
      }
    }

    if (this.comboWrap) {
      this.comboWrap.dataset.active = comboActive ? 'true' : 'false';
    }
    if (this.comboMult) {
      this.comboMult.textContent = `${comboMultiplier.toFixed(2)}×`;
    }
    if (this.comboChain) {
      this.comboChain.textContent =
        comboChain > 0 ? `${comboChain} link${comboChain === 1 ? '' : 's'}` : '';
    }
  }

  updateContract(title: string, progress: string, complete: boolean): void {
    if (this.contractTitle) this.contractTitle.textContent = title;
    if (this.contractProgress) this.contractProgress.textContent = progress;
    if (this.contractWrap) {
      this.contractWrap.dataset.state = complete ? 'complete' : 'active';
    }
  }
}
